import { scaleNutrients, type NutrientValue } from './nutrients';

export type MealLocalizedLabel = {
    language: string;
    value: string;
};

export type MealProductSize = {
    id: string;
    label: string;
    grams: number;
    labels?: MealLocalizedLabel[];
};

export type MealPortionMode = 'size' | 'grams';

export const MEAL_GRAMS_MIN = 1;
export const MEAL_GRAMS_MAX = 1000;
export const MEAL_GRAMS_DEFAULT = 100;

export function scaleToPer100gFactor(fromReferenceGrams: number | null | undefined): number {
    const from = fromReferenceGrams != null && fromReferenceGrams > 0 ? fromReferenceGrams : MEAL_GRAMS_DEFAULT;
    return MEAL_GRAMS_DEFAULT / from;
}

export function roundKcalPer100g(kcal: number): number {
    return Math.round(kcal * 10) / 10;
}

export function parseKcalPer100gInput(raw: string): number | null {
    const normalized = raw.trim().replace(',', '.');
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return roundKcalPer100g(parsed);
}

export function isValidKcalPer100gInput(raw: string): boolean {
    return parseKcalPer100gInput(raw) != null;
}

export function sanitizeKcalPer100gInput(raw: string): string {
    const normalized = raw.replace(',', '.');
    if (normalized === '' || /^\d*(\.\d*)?$/.test(normalized)) return normalized;
    return normalized.slice(0, -1);
}

export function scaleKcalToPer100g(kcal: number, fromReferenceGrams: number | null | undefined): number {
    if (!Number.isFinite(kcal) || kcal <= 0) return 0;
    return Math.max(0.1, roundKcalPer100g(kcal * scaleToPer100gFactor(fromReferenceGrams)));
}

export function scaleNutrientsToPer100g(nutrients: NutrientValue[], fromReferenceGrams: number | null | undefined): NutrientValue[] {
    const factor = scaleToPer100gFactor(fromReferenceGrams);
    if (Math.abs(factor - 1) < 1e-9) return nutrients;
    return scaleNutrients(nutrients, factor);
}

export function clampMealGrams(grams: number): number {
    if (!Number.isFinite(grams)) return MEAL_GRAMS_DEFAULT;
    return Math.min(MEAL_GRAMS_MAX, Math.max(MEAL_GRAMS_MIN, Math.round(grams)));
}

export function defaultMealSizeId(sizes: MealProductSize[], preferredId?: string | null): string | null {
    if (!sizes.length) return null;
    if (preferredId && sizes.some(size => size.id === preferredId)) return preferredId;
    return sizes[0]?.id ?? null;
}

export function mealSizeById(sizes: MealProductSize[], id: string | null): MealProductSize | null {
    if (!id) return null;
    return sizes.find(size => size.id === id) ?? null;
}

export function computeMealEffectiveGrams(
    mode: MealPortionMode,
    sizes: MealProductSize[],
    selectedSizeId: string | null,
    chipMultiplier: number,
    customGrams: number,
): number {
    if (mode === 'grams') return clampMealGrams(customGrams);
    const size = mealSizeById(sizes, selectedSizeId) ?? mealSizeById(sizes, defaultMealSizeId(sizes));
    if (!size) return clampMealGrams(customGrams);
    const portion = chipMultiplier > 0 ? chipMultiplier : 1;
    const effective = size.grams * portion;
    return Number.isFinite(effective) && effective > 0 ? effective : MEAL_GRAMS_DEFAULT;
}

export function computeMealScaleFactor(referenceGrams: number, effectiveGrams: number): number {
    const ref = referenceGrams > 0 ? referenceGrams : MEAL_GRAMS_DEFAULT;
    const effective = effectiveGrams > 0 ? effectiveGrams : ref;
    return effective / ref;
}

export function formatMealPortionSummary(
    mode: MealPortionMode,
    sizes: MealProductSize[],
    selectedSizeId: string | null,
    chipMultiplier: number,
    customGrams: number,
    formatPortionLabel: (multiplier: number) => string,
): string {
    if (mode === 'grams') return `${clampMealGrams(customGrams)} g`;
    const size = mealSizeById(sizes, selectedSizeId);
    const effectiveGrams = computeMealEffectiveGrams(mode, sizes, selectedSizeId, chipMultiplier, customGrams);
    if (!size) return `${effectiveGrams} g`;
    return `${formatPortionLabel(chipMultiplier)} ${size.label.toLowerCase()} · ${effectiveGrams} g`;
}

export function createInitialMealPortionState(
    sizes: MealProductSize[],
    defaultSizeId?: string | null,
): {
    portionMode: MealPortionMode;
    selectedSizeId: string | null;
    chipMultiplier: number;
    customGrams: number;
} {
    const selectedSizeId = defaultMealSizeId(sizes, defaultSizeId);
    const defaultSize = mealSizeById(sizes, selectedSizeId);
    return {
        portionMode: sizes.length ? 'size' : 'grams',
        selectedSizeId,
        chipMultiplier: 1,
        customGrams: defaultSize?.grams ?? MEAL_GRAMS_DEFAULT,
    };
}
